import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @typedef {Object} Position
 * @property {number} x - The x coordinate in pixels
 * @property {number} y - The y coordinate in pixels
 */

/**
 * @typedef {Object} UseDragOptions
 * @property {function(Position)} [onDragEnd] - Callback fired when dragging ends
 * @property {function(Position)} [onDragMove] - Callback fired during dragging for real-time updates
 * @property {Position} [initialPosition={ x: 0, y: 0 }] - Initial position of the draggable element
 * @property {number} [elementWidth=0] - Width of the draggable element for boundary calculations
 * @property {number} [elementHeight=0] - Height of the draggable element for boundary calculations
 */

/**
 * @typedef {Object} UseDragReturn
 * @property {React.RefCallback} ref - Ref callback to attach to the draggable element
 * @property {boolean} isDragging - Whether the element is currently being dragged
 * @property {Position} position - Current position of the element
 */

/**
 * A custom React hook for making elements draggable with mouse and touch support.
 *
 * Features:
 * - Mouse and touch event support
 * - Viewport boundary constraints
 * - Optimized position updates (only calls onDragEnd when dragging finishes)
 * - Configurable element dimensions for accurate boundary calculations
 * - Automatic cursor management during drag operations
 *
 * @param {UseDragOptions} options - Configuration options for the drag behavior
 * @returns {UseDragReturn} Object containing ref callback, drag state, and current position
 *
 * @example
 * const { ref: dragRef, isDragging, position } = useDrag({
 *   onDragEnd: (newPosition) => {
 *     console.log('Final position:', newPosition);
 *   },
 *   onDragMove: (currentPosition) => {
 *     console.log('Current position:', currentPosition);
 *   },
 *   initialPosition: { x: 100, y: 50 },
 *   elementWidth: 300,
 *   elementHeight: 200
 * });
 *
 * return (
 *   <div
 *     ref={dragRef}
 *     style={{
 *       position: 'absolute',
 *       left: position.x,
 *       top: position.y,
 *       cursor: isDragging ? 'grabbing' : 'grab'
 *     }}
 *   >
 *     Drag me!
 *   </div>
 * );
 */
export function useDrag({
  onDragEnd,
  onDragMove,
  initialPosition = { x: 0, y: 0 },
  elementWidth = 0,
  elementHeight = 0,
}) {
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const elementStart = useRef({ x: 0, y: 0 });
  const currentPosition = useRef(initialPosition);
  const [active, setActive] = useState(false);
  const cleanupRef = useRef(null);

  useEffect(() => {
    currentPosition.current = initialPosition;
  }, [initialPosition.x, initialPosition.y]);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging.current) return;

      const deltaX = e.clientX - dragStart.current.x;
      const deltaY = e.clientY - dragStart.current.y;

      const newPosition = {
        x: elementStart.current.x + deltaX,
        y: elementStart.current.y + deltaY,
      };

      const maxX = elementWidth > 0 ? window.innerWidth - elementWidth : window.innerWidth;
      const maxY = elementHeight > 0 ? window.innerHeight - elementHeight : window.innerHeight;

      newPosition.x = Math.max(0, Math.min(newPosition.x, maxX));
      newPosition.y = Math.max(0, Math.min(newPosition.y, maxY));

      currentPosition.current = newPosition;

      if (onDragMove) {
        onDragMove(newPosition);
      }
    },
    [onDragMove, elementWidth, elementHeight],
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging.current || !e.touches[0]) return;

      e.preventDefault();

      const deltaX = e.touches[0].clientX - dragStart.current.x;
      const deltaY = e.touches[0].clientY - dragStart.current.y;

      const newPosition = {
        x: elementStart.current.x + deltaX,
        y: elementStart.current.y + deltaY,
      };

      const maxX = elementWidth > 0 ? window.innerWidth - elementWidth : window.innerWidth;
      const maxY = elementHeight > 0 ? window.innerHeight - elementHeight : window.innerHeight;

      newPosition.x = Math.max(0, Math.min(newPosition.x, maxX));
      newPosition.y = Math.max(0, Math.min(newPosition.y, maxY));

      currentPosition.current = newPosition;

      if (onDragMove) {
        onDragMove(newPosition);
      }
    },
    [onDragMove, elementWidth, elementHeight],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return;

    isDragging.current = false;
    setActive(false);

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleMouseUp);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (onDragEnd) {
      onDragEnd(currentPosition.current);
    }
  }, [handleMouseMove, handleTouchMove, onDragEnd]);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();

      isDragging.current = true;
      setActive(true);

      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
      };
      elementStart.current = { ...currentPosition.current };

      document.body.style.cursor = 'move';
      document.body.style.userSelect = 'none';

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp],
  );

  const handleTouchStart = useCallback(
    (e) => {
      if (!e.touches[0]) return;

      e.preventDefault();

      isDragging.current = true;
      setActive(true);

      dragStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      elementStart.current = { ...currentPosition.current };

      document.body.style.cursor = 'move';
      document.body.style.userSelect = 'none';

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
    },
    [handleTouchMove, handleMouseUp],
  );

  const refCallback = useCallback(
    (node) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      if (!node) return;

      node.addEventListener('mousedown', handleMouseDown);
      node.addEventListener('touchstart', handleTouchStart, { passive: false });

      cleanupRef.current = () => {
        node.removeEventListener('mousedown', handleMouseDown);
        node.removeEventListener('touchstart', handleTouchStart);
      };
    },
    [handleMouseDown, handleTouchStart],
  );

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove]);

  return {
    ref: refCallback,
    isDragging: active,
    position: currentPosition.current,
  };
}
